/**
 * Shared display formatters for Control Tower.
 *
 * Every page must use these instead of ad-hoc `toLocaleString` calls so
 * timestamps, numbers, and units stay consistent across the SPA.
 */

const KST = "Asia/Seoul";

function toDate(value?: string | number | Date | null): Date | null {
  if (value === null || value === undefined || value === "") return null;
  const d = value instanceof Date ? value : new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
}

/** `2026-09-22 20:16:24` (KST). Options can drop the date or seconds. */
export function fmtKst(
  value?: string | number | Date | null,
  opts: { date?: boolean; seconds?: boolean } = {},
): string {
  const { date = true, seconds = true } = opts;
  const d = toDate(value);
  if (!d) return "—";
  const full = d.toLocaleString("sv-SE", { timeZone: KST, hour12: false });
  if (date && seconds) return full;
  if (date) return full.slice(0, 16);
  return seconds ? full.slice(11) : full.slice(11, 16);
}

/** `09-22 20:16` (KST) — compact form for dense tables. */
export function fmtKstShort(value?: string | number | Date | null): string {
  const d = toDate(value);
  if (!d) return "—";
  const full = d.toLocaleString("sv-SE", { timeZone: KST, hour12: false });
  return `${full.slice(5, 10)} ${full.slice(11, 16)}`;
}

/** `3분 전`, `2시간 전`, `방금` — falls back to `—`. */
export function fmtRelative(value?: string | number | Date | null): string {
  const d = toDate(value);
  if (!d) return "—";
  const diff = Date.now() - d.getTime();
  const abs = Math.abs(diff);
  const suffix = diff >= 0 ? "전" : "후";
  if (abs < 45_000) return "방금";
  const units: Array<[number, number, string]> = [
    [3_600_000, 60_000, "분"],
    [86_400_000, 3_600_000, "시간"],
    [2_592_000_000, 86_400_000, "일"],
  ];
  for (const [limit, divisor, label] of units) {
    if (abs < limit) return `${Math.floor(abs / divisor)}${label} ${suffix}`;
  }
  return `${Math.floor(abs / 2_592_000_000)}개월 ${suffix}`;
}

/** Compact magnitude: `1.41B`, `12.3M`, `4.2K`. */
export function fmtCompact(value: unknown, digits = 2): string {
  const n = typeof value === "number" ? value : Number(value);
  if (value === null || value === undefined || Number.isNaN(n)) return "—";
  if (Math.abs(n) < 1000) return fmtNumber(n, { digits: Math.abs(n) < 1 ? 4 : digits });
  return n.toLocaleString("en-US", {
    notation: "compact",
    maximumFractionDigits: digits,
  });
}

/** Fixed-decimal number with thousand separators. */
export function fmtNumber(
  value: unknown,
  opts: { digits?: number; sign?: boolean; fallback?: string } = {},
): string {
  const { digits = 2, sign = false, fallback = "—" } = opts;
  const n = typeof value === "number" ? value : Number(value);
  if (value === null || value === undefined || Number.isNaN(n)) return fallback;
  const fixed = n.toLocaleString("en-US", {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  });
  return sign && n > 0 ? `+${fixed}` : fixed;
}

/**
 * Value-aware formatting for financial observations:
 * large magnitudes compact, small ratios keep precision, integers clean.
 */
export function fmtValue(
  value: unknown,
  opts: { digits?: number; unit?: string | null; sign?: boolean } = {},
): string {
  const { digits = 2, unit, sign = false } = opts;
  const n = typeof value === "number" ? value : Number(value);
  if (value === null || value === undefined || Number.isNaN(n)) return "—";
  const abs = Math.abs(n);
  let text: string;
  if (abs >= 100_000) text = fmtCompact(n, 2);
  else if (abs >= 1000) text = fmtNumber(n, { digits: 0 });
  else if (abs >= 1) text = fmtNumber(n, { digits, sign });
  else if (abs === 0) text = "0";
  else text = fmtNumber(n, { digits: 4, sign });
  if (abs >= 1000 && sign && n > 0) text = `+${text}`;
  return unit ? `${text} ${unit}` : text;
}

/** Percentage points (`65.3` → `65.3%`). */
export function fmtPct(
  value: unknown,
  opts: { digits?: number; sign?: boolean } = {},
): string {
  const { digits = 1, sign = false } = opts;
  const n = typeof value === "number" ? value : Number(value);
  if (value === null || value === undefined || Number.isNaN(n)) return "—";
  return `${fmtNumber(n, { digits, sign })}%`;
}

export function fmtBytes(value?: number | null): string {
  if (value === null || value === undefined || value <= 0) return "0 B";
  const units = ["B", "KB", "MB", "GB", "TB"];
  let index = 0;
  let scaled = value;
  while (scaled >= 1024 && index < units.length - 1) {
    scaled /= 1024;
    index += 1;
  }
  return `${scaled.toFixed(scaled >= 100 ? 0 : 1)} ${units[index]}`;
}

/** `95초` → `1분 35초`, `3.2시간`. */
export function fmtDuration(seconds?: number | null): string {
  if (seconds === null || seconds === undefined) return "—";
  if (seconds < 0) return "시각 확인 필요";
  if (seconds < 60) return `${Math.round(seconds)}초`;
  if (seconds < 3600) {
    return `${Math.floor(seconds / 60)}분 ${Math.round(seconds % 60)}초`;
  }
  if (seconds < 86_400) return `${(seconds / 3600).toFixed(1)}시간`;
  return `${(seconds / 86_400).toFixed(1)}일`;
}

/** Elapsed seconds since an ISO timestamp (null when unparseable). */
export function ageSeconds(value?: string | number | Date | null): number | null {
  const d = toDate(value);
  if (!d) return null;
  return (Date.now() - d.getTime()) / 1000;
}

/** Signed PnL/price with tone class. */
export function toneOf(value: unknown): "up" | "down" | "flat" {
  const n = typeof value === "number" ? value : Number(value);
  if (Number.isNaN(n) || n === 0) return "flat";
  return n > 0 ? "up" : "down";
}
